/**
 * Card Direct Add to Cart Functionality
 * Handles direct add to cart bypassing quick-add modal for products with variant selectors
 */

class CardDirectAddToCart {
  // Constants for better maintainability
  static SELECTORS = {
    variantSelect: '.card-variant-select[data-update-form="true"]',
    productForm: 'product-form[data-section-id]',
    cardWrapper: '.card-wrapper',
    gridItem: '.grid__item',
    sliderSlide: '.slider__slide',
    submitButton: '.quick-add__submit',
    variantInput: 'input[name="id"]',
    priceElement: '.price-item--regular'
  };

  static EVENTS = {
    sectionLoad: 'shopify:section:load',
    sliderChange: 'slider:change',
    change: 'change'
  };

  static DEBOUNCE_DELAY = 100;

  constructor() {
    this.initialized = new Set();
    this.debounceTimer = null;
    this.init();
  }

  // Debounce utility method
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  init() {
    this.setupVariantSelectors();
    this.setupAddToCartButtons();
    if (!this.observerSetup) {
      this.setupEventListeners();
      this.observerSetup = true;
    }
  }

  setupEventListeners() {
    // Debounced reinit for performance
    const debouncedReinit = () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.init(), CardDirectAddToCart.DEBOUNCE_DELAY);
    };

    document.addEventListener(CardDirectAddToCart.EVENTS.sectionLoad, debouncedReinit);
    document.addEventListener(CardDirectAddToCart.EVENTS.sliderChange, debouncedReinit);

    // Efficient MutationObserver
    const observer = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === Node.ELEMENT_NODE && (
            node.matches?.('.card-wrapper, .grid__item, .slider__slide') ||
            node.querySelector?.('.card-variant-select, .quick-add__submit')
          )
        )
      );
      
      if (hasRelevantChanges) debouncedReinit();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupVariantSelectors() {
    const selectors = document.querySelectorAll(CardDirectAddToCart.SELECTORS.variantSelect);
    
    selectors.forEach((selector) => {
      if (!selector.dataset.productId || !selector.dataset.optionPosition) {
        console.warn('Variant selector missing required data attributes:', selector);
        return;
      }

      const selectorId = `${selector.dataset.productId}-${selector.dataset.optionPosition}`;
      
      // Skip if already initialized
      if (this.initialized.has(selectorId)) return;
      
      // Add event listener for variant changes with debounce
      const debouncedHandler = this.debounce((event) => {
        // Check if the main variant system is processing
        if (window.isVariantProcessing) {
          setTimeout(() => {
            if (!window.isVariantProcessing) {
              this.updateProductForm(event.target);
              this.updateCardVariantPrice(event.target);
            }
          }, 200);
          return;
        }
        
        this.updateProductForm(event.target);
        this.updateCardVariantPrice(event.target);
      }, 100);
      
      selector.addEventListener(CardDirectAddToCart.EVENTS.change, debouncedHandler);
      
      this.initialized.add(selectorId);
    });
  }

  setupAddToCartButtons() {
    const forms = document.querySelectorAll(CardDirectAddToCart.SELECTORS.productForm);
    
    forms.forEach((form) => {
      const cardWrapper = this.findCardContainer(form);
      if (!cardWrapper) return;

      const variantSelector = cardWrapper.querySelector(CardDirectAddToCart.SELECTORS.variantSelect);
      if (!variantSelector) return;

      const formElement = form.querySelector('form');
      const formId = formElement?.id;
      if (!formId || this.initialized.has(formId)) return;

      const submitButton = form.querySelector(CardDirectAddToCart.SELECTORS.submitButton);
      if (submitButton) {
        this.convertToDirectForm(submitButton);
        // Initialize with the selected variant (cheapest available)
        this.updateProductForm(variantSelector);
      }
      
      this.initialized.add(formId);
    });
  }

  convertToDirectForm(submitButton) {
    const modalOpener = submitButton.closest('modal-opener');
    if (modalOpener) {
      modalOpener.insertAdjacentElement('afterend', submitButton);
      modalOpener.remove();
    }
    
    submitButton.removeAttribute('aria-haspopup');
    submitButton.removeAttribute('data-product-url');
    submitButton.type = 'submit';
  }

  findCardContainer(element) {
    return element.closest(CardDirectAddToCart.SELECTORS.cardWrapper) || 
           element.closest(CardDirectAddToCart.SELECTORS.gridItem) || 
           element.closest(CardDirectAddToCart.SELECTORS.sliderSlide);
  }

  updateProductForm(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    if (!selectedOption) return;

    const variantId = selectedOption.dataset.variantId;
    const variantAvailable = selectedOption.dataset.variantAvailable === 'true';
    
    if (!variantId) return;

    const cardContainer = this.findCardContainer(selectElement);
    if (!cardContainer) return;

    const productForm = cardContainer.querySelector('product-form');
    if (!productForm) return;

    this.updateVariantInput(productForm, variantId, variantAvailable);
    this.updateSubmitButton(productForm, variantAvailable);
  }

  updateVariantInput(productForm, variantId, variantAvailable) {
    const variantInput = productForm.querySelector(CardDirectAddToCart.SELECTORS.variantInput);
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.disabled = !variantAvailable;
    }
  }

  updateSubmitButton(productForm, variantAvailable) {
    const submitButton = productForm.querySelector(CardDirectAddToCart.SELECTORS.submitButton);
    if (!submitButton) return;

    const buttonText = submitButton.querySelector('span:first-child') || submitButton.querySelector('span');
    
    submitButton.disabled = !variantAvailable;
    
    if (buttonText) {
      buttonText.textContent = variantAvailable 
        ? (window.variantStrings?.addToCart || 'Add to cart')
        : (window.variantStrings?.soldOut || 'Sold out');
    }
  }

  updateCardVariantPrice(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    if (!selectedOption?.dataset.variantPrice) return;
    
    const cardContainer = this.findCardContainer(selectElement);
    if (!cardContainer) return;
    
    const variantPrice = selectedOption.dataset.variantPrice;
    const variantComparePrice = selectedOption.dataset.variantComparePrice;
    
    // Format the main price
    const formattedPrice = this.formatPrice(variantPrice);
    
    // Find the price container
    const priceContainer = cardContainer.querySelector('.price');
    if (!priceContainer) return;
    
    // Check if product is on sale (price__sale is visible)
    const saleContainer = priceContainer.querySelector('.price__sale');
    const regularContainer = priceContainer.querySelector('.price__regular');
    
    const isOnSale = saleContainer && window.getComputedStyle(saleContainer).display !== 'none';
    
    if (isOnSale) {
      // Product is on sale - update sale price
      const salePriceElement = saleContainer.querySelector('.price-item--sale');
      if (salePriceElement) {
        salePriceElement.textContent = formattedPrice;
      }
      
      // Update compare-at price if available
      if (variantComparePrice && variantComparePrice !== 'null' && variantComparePrice !== '0') {
        const formattedComparePrice = this.formatPrice(variantComparePrice);
        const comparePriceElement = saleContainer.querySelector('.price-item--regular');
        if (comparePriceElement) {
          comparePriceElement.textContent = formattedComparePrice;
        }
      }
    } else {
      // Product is NOT on sale - update regular price
      const regularPriceElement = regularContainer?.querySelector('.price-item--regular');
      if (regularPriceElement) {
        regularPriceElement.textContent = formattedPrice;
      }
    }
  }

  formatPrice(variantPrice) {
    const priceInCents = parseInt(variantPrice);
    const priceInPesos = Math.round(priceInCents / 100);
    return '$' + priceInPesos.toLocaleString('es-CL') + ' CLP';
  }
}

// Initialize efficiently
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CardDirectAddToCart());
} else {
  new CardDirectAddToCart();
}